/**
 * Organization structure — the legal entity / company / branch / department
 * hierarchy the whole tenancy model rests on.
 *
 * The specification lists `GET /v1/organization/structure` returning a tree.
 * That endpoint does not exist; the five level endpoints do. So the tree is
 * composed here from the five real lists rather than calling a phantom one —
 * five small parallel reads of a structure that is a handful of rows for even
 * a large tenant.
 *
 * Arabic and English (C7): every word from the catalogs, counts as plurals,
 * a country named in the interface's language; the tree indents on the
 * inline-start side, so it reads from the right in Arabic.
 */
import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  PageHeader,
  Button,
  Skeleton,
  EmptyState,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { formatQuantity } from "@/lib/money";
import { countryName } from "@/lib/reference";
import type {
  BranchRow,
  CompanyRow,
  DepartmentRow,
  LegalEntityRow,
  LocationRow,
} from "./types";

export function OrgStructurePage() {
  const { t, i18n } = useTranslation();
  const entities = useApiList<LegalEntityRow>(
    ["org", "legal-entities"],
    "/legal-entities",
  );
  const companies = useApiList<CompanyRow>(["org", "companies"], "/companies");
  const branches = useApiList<BranchRow>(["org", "branches"], "/branches");
  const departments = useApiList<DepartmentRow>(
    ["org", "departments"],
    "/departments",
  );
  const locations = useApiList<LocationRow>(["org", "locations"], "/locations");

  const loading =
    entities.isLoading ||
    companies.isLoading ||
    branches.isLoading ||
    departments.isLoading ||
    locations.isLoading;

  const tree = useMemo(() => {
    const companyRows = companies.data?.items ?? [];
    const branchRows = branches.data?.items ?? [];
    const departmentRows = departments.data?.items ?? [];
    const locationRows = locations.data?.items ?? [];

    return (entities.data?.items ?? []).map((entity) => {
      const owned = companyRows.filter((c) => c.legal_entity_id === entity.id);
      return {
        entity,
        companies: owned.map((company) => {
          const companyBranches = branchRows.filter(
            (b) => b.company_id === company.id,
          );
          return {
            company,
            departments: departmentRows.filter(
              (d) => d.company_id === company.id,
            ),
            branches: companyBranches.map((branch) => ({
              branch,
              locations: locationRows.filter((l) => l.branch_id === branch.id),
            })),
          };
        }),
      };
    });
  }, [
    entities.data,
    companies.data,
    branches.data,
    departments.data,
    locations.data,
  ]);

  // Rows whose parent is missing would otherwise vanish from the tree without
  // trace. An orphan is a real data problem, so it is surfaced rather than
  // quietly filtered out.
  const orphanCompanies = useMemo(() => {
    const entityIds = new Set((entities.data?.items ?? []).map((e) => e.id));
    return (companies.data?.items ?? []).filter(
      (c) => !entityIds.has(c.legal_entity_id),
    );
  }, [entities.data, companies.data]);

  if (loading) return <Skeleton rows={12} />;

  const counts = {
    entities: entities.data?.total ?? 0,
    companies: companies.data?.total ?? 0,
    branches: branches.data?.total ?? 0,
    departments: departments.data?.total ?? 0,
    locations: locations.data?.total ?? 0,
  };

  return (
    <div>
      <PageHeader
        title={t("org.structure.title")}
        subtitle={t("org.structure.subtitle")}
        actions={
          <Link to="/app/organization/legal-entities">
            <Button variant="primary">{t("org.structure.manage")}</Button>
          </Link>
        }
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard
          label={t("org.legalEntities.title")}
          value={formatQuantity(counts.entities, 0)}
        />
        <StatCard
          label={t("org.companies.title")}
          value={formatQuantity(counts.companies, 0)}
        />
        <StatCard
          label={t("org.branches.title")}
          value={formatQuantity(counts.branches, 0)}
        />
        <StatCard
          label={t("org.departments.title")}
          value={formatQuantity(counts.departments, 0)}
        />
        <StatCard
          label={t("org.locations.title")}
          value={formatQuantity(counts.locations, 0)}
        />
      </div>

      {tree.length === 0 ? (
        <EmptyState
          title={t("org.structure.emptyTitle")}
          message={t("org.structure.emptyMessage")}
          action={
            <Link to="/app/organization/legal-entities">
              <Button variant="primary">{t("org.structure.add")}</Button>
            </Link>
          }
        />
      ) : (
        <Card title={t("org.structure.hierarchy")}>
          <ul className="atlas-tree">
            {tree.map(({ entity, companies: owned }) => (
              <li key={entity.id}>
                <div className="atlas-tree__node">
                  <Link
                    to={`/app/organization/legal-entities/${entity.id}`}
                    className="atlas-tree__label"
                  >
                    {entity.name}
                  </Link>
                  <span className="atlas-muted" style={{ fontSize: 12 }}>
                    {countryName(entity.registration_country, i18n.language)} ·{" "}
                    {entity.currency_code}
                  </span>
                  <StatusBadge status={entity.status} type="LegalEntity" />
                </div>

                {owned.length === 0 ? (
                  <div
                    className="atlas-muted"
                    style={{ fontSize: 13, paddingInlineStart: 20 }}
                  >
                    {t("org.structure.noCompany")}
                  </div>
                ) : (
                  <ul>
                    {owned.map(
                      ({
                        company,
                        branches: companyBranches,
                        departments: companyDepartments,
                      }) => (
                        <li key={company.id}>
                          <div className="atlas-tree__node">
                            <Link
                              to={`/app/organization/companies/${company.id}`}
                              className="atlas-tree__label"
                            >
                              {company.name}
                            </Link>
                            <span
                              className="atlas-mono atlas-muted"
                              style={{ fontSize: 12 }}
                            >
                              {company.code}
                            </span>
                            <StatusBadge
                              status={company.status}
                              type="Company"
                            />
                            <span
                              className="atlas-muted"
                              style={{ fontSize: 12 }}
                            >
                              {t("org.structure.departments", {
                                count: companyDepartments.length,
                              })}{" "}
                              ·{" "}
                              {t("org.structure.branches", {
                                count: companyBranches.length,
                              })}
                            </span>
                          </div>
                          {companyBranches.length > 0 && (
                            <ul>
                              {companyBranches.map(
                                ({ branch, locations: branchLocations }) => (
                                  <li key={branch.id}>
                                    <div className="atlas-tree__node">
                                      <span className="atlas-tree__label">
                                        {branch.name}
                                      </span>
                                      <span
                                        className="atlas-mono atlas-muted"
                                        style={{ fontSize: 12 }}
                                      >
                                        {branch.code}
                                      </span>
                                      {branchLocations.length > 0 && (
                                        <span
                                          className="atlas-muted"
                                          style={{ fontSize: 12 }}
                                        >
                                          {t("org.structure.locations", {
                                            count: branchLocations.length,
                                          })}
                                        </span>
                                      )}
                                    </div>
                                  </li>
                                ),
                              )}
                            </ul>
                          )}
                        </li>
                      ),
                    )}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {orphanCompanies.length > 0 && (
        <Card title={t("org.structure.orphansTitle")} className="mt-4">
          <p className="atlas-muted" style={{ fontSize: 13 }}>
            {t("org.structure.orphansBody")}
          </p>
          <ul>
            {orphanCompanies.map((c) => (
              <li key={c.id}>
                {c.name}{" "}
                <span className="atlas-mono atlas-muted">({c.code})</span>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
